import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

/* ── Categories ── */
const categories = ['All', 'Analytics', 'Real-Time', 'Risk', 'KPI', 'Data Unification', 'Security', 'No-Code', 'Edge AI'];

/* ── Posts data ── */
const allPosts = [
  {
    tag: 'Analytics',
    title: 'Turning Complex Business Data Into Clear Decisions',
    desc: 'How predictive intelligence converts raw, multi-source data into forward-looking, actionable insights that drive confident business decisions.',
    date: 'Feb 20, 2026',
    readTime: '8 min read',
    featured: true,
    image: process.env.PUBLIC_URL + '/blog_result.webp',
    content: [
      `Every business today generates vast amounts of data, from sales figures and customer behaviors to operational metrics and financial performance, and while collecting this information is relatively easy, turning it into meaningful insights that drive better decisions remains a significant challenge. Many organizations find themselves overwhelmed by spreadsheets, dashboards, and disconnected reporting tools that show what happened in the past but offer little guidance for the future, and by the time leadership teams review these reports, critical opportunities may have already been missed. Predictive intelligence changes this dynamic entirely by enabling businesses to transform raw, multi-source data into forward-looking insights that highlight what is likely to happen and what actions should be taken. When data from various systems is consolidated into a unified intelligence platform, hidden patterns and correlations begin to surface, helping teams identify trends, operational inefficiencies, and market shifts before they become problems. For example, a retail organization analyzing sales across multiple regions can move beyond understanding last month’s revenue decline to predicting next month’s performance based on seasonal trends, buying behavior, supply constraints, and marketing engagement, and it can even receive actionable recommendations on how to improve outcomes immediately.`,
      `The shift from reactive reporting to proactive decision-making provides measurable competitive advantage because it allows businesses to anticipate challenges, optimize resources, and act with confidence. Real-time monitoring further enhances this capability by allowing leadership teams to see the current status of key performance indicators continuously rather than waiting for weekly or monthly reports, and this ongoing visibility ensures that decisions are timely and well-informed. Predictive analytics also democratizes data understanding by providing visual dashboards and intuitive interfaces that enable business users, not just technical data scientists, to interpret insights, explore trends, and act without needing to write a single line of code. By transforming complex datasets into clear, actionable information, predictive intelligence increases operational efficiency, reduces risk, and accelerates decision cycles, allowing organizations to allocate resources more strategically and pursue growth opportunities with confidence. The clarity offered by AI-driven insights reduces uncertainty, aligns teams around shared objectives, and fosters a culture of informed decision-making where intuition is supported by evidence.`,
      `As businesses grow, their data complexity often increases, but predictive analytics scales alongside operations, ensuring that as data volumes expand, the ability to extract meaningful insights is never compromised. Ultimately, success in modern business is no longer about collecting data for the sake of reporting; it is about converting information into foresight, and foresight into action, and organizations that master this transformation gain a decisive edge over competitors. Predictive intelligence enables companies to make better decisions, respond faster to market changes, and strategically navigate complexity, and by embracing these capabilities, businesses ensure that every piece of data collected contributes meaningfully to growth, efficiency, and long-term sustainability.`
    ],
  },
  {
    tag: 'Real-Time',
    title: 'Real-Time Analytics for Faster Business Growth',
    desc: 'Why instant visibility into streaming data accelerates decisions, improves customer experience, and drives measurable growth.',
    date: 'Feb 18, 2026',
    readTime: '9 min read',
    featured: false,
    image: process.env.PUBLIC_URL + '/blog (2)_result.webp',
    content: [
      `In today’s fast-paced digital landscape, businesses generate enormous amounts of data every second, from website interactions and social media engagements to CRM updates and operational metrics, and while this data is valuable, it often goes underutilized because organizations struggle to interpret it quickly enough to make actionable decisions, which is where real-time analytics fundamentally changes the game. Unlike traditional reporting that only shows what happened after the fact, real-time analytics provides instant visibility into ongoing trends, behaviors, and performance metrics, enabling teams to respond immediately to emerging opportunities and challenges before they impact business outcomes.`,
      `By aggregating data from multiple sources into a single, AI-powered platform, organizations can track user behavior, conversion rates, sales trends, and operational KPIs simultaneously, and this unified perspective ensures that leaders are not relying on fragmented or outdated information when making critical decisions. Real-time analytics accelerates decision-making by eliminating the delays associated with manual data aggregation and interpretation, allowing teams to continuously monitor key performance indicators and take proactive action, which translates into faster response times, improved operational efficiency, and measurable growth.`,
      `The cumulative effect of real-time analytics is faster, more confident decision-making, better customer experiences, and a tangible competitive edge; it is a strategic enabler that transforms raw data into actionable insight, accelerates business processes, and empowers organizations to operate with clarity, foresight, and efficiency.`
    ],
  },
  {
    tag: 'Risk',
    title: 'How Predictive Modeling Reduces Business Risk',
    desc: 'Using AI to forecast exposures, detect anomalies, and simulate scenarios that help organizations avoid costly disruptions.',
    date: 'Feb 12, 2026',
    readTime: '8 min read',
    featured: false,
    image: process.env.PUBLIC_URL + '/blog (3)_result.webp',
    content: [
      `Every business faces risks, whether it’s financial exposure, operational inefficiencies, market fluctuations, or customer churn, and while risk is inevitable, the difference between successful organizations and struggling ones lies in how effectively they anticipate and mitigate those risks before they escalate into serious problems. Traditional risk management often relies on historical reports, spreadsheets, and manual analysis, which can highlight issues only after they occur, leaving organizations reactive rather than proactive, and in a fast-paced world, reacting too late can be costly. Predictive modeling changes this by using artificial intelligence and advanced analytics to examine vast amounts of data from multiple sources, identify patterns, detect anomalies, and forecast potential outcomes, enabling decision-makers to act in advance and avoid unnecessary losses.`,
      `Predictive modeling also provides operational visibility, enabling businesses to identify bottlenecks, inefficiencies, or areas of potential failure in supply chains, production lines, or service delivery, and by simulating different scenarios, leaders can test the impact of decisions before committing resources, reducing uncertainty and increasing confidence in outcomes. The real power of predictive modeling lies in its ability to combine multiple variables and produce insights that are not obvious through traditional analysis, which allows leaders to make more informed and strategic choices.`,
      `By integrating predictive modeling into daily operations, businesses can transform uncertainty into clarity, turning data into foresight, and foresight into actionable strategies that protect assets, optimize processes, and strengthen competitive advantage.`
    ],
  },
  {
    tag: 'KPI',
    title: 'The Power of AI-Driven KPI Monitoring',
    desc: 'Turning KPIs into dynamic, predictive indicators that guide strategy and operations in real time.',
    date: 'Feb 8, 2026',
    readTime: '7 min read',
    featured: false,
    image: process.env.PUBLIC_URL + '/blog (4)_result.webp',
    content: [
      `In any business, key performance indicators, or KPIs, are the compass that guides strategy, operations, and growth, yet many organizations struggle to track them effectively because traditional monitoring methods rely on fragmented reports, manual data entry, and delayed updates, which can result in missed opportunities, slow responses, and uninformed decisions. AI-driven KPI monitoring transforms this process by providing real-time, intelligent insights that automatically track, analyze, and interpret data across multiple systems and departments, allowing leaders to see performance as it happens rather than weeks or months after the fact.`,
      `With AI monitoring, KPIs are no longer static numbers on a spreadsheet but dynamic metrics that adapt as business conditions evolve, highlighting trends, anomalies, and opportunities that might otherwise go unnoticed, and this enables teams to act quickly, prevent problems, and capitalize on emerging strengths. Continuous monitoring also fosters accountability and transparency across teams, creating a culture of performance optimization and data-driven decision-making.`,
    ],
  },
  {
    tag: 'Data Unification',
    title: 'Unifying Multi-Source Data for Smarter Insights',
    desc: 'How consolidating disparate datasets creates a single source of truth and unlocks advanced analytics.',
    date: 'Feb 4, 2026',
    readTime: '9 min read',
    featured: false,
    image: process.env.PUBLIC_URL + '/blog (5)_result.webp',
    content: [
      `In modern business environments, data comes from countless sources, including sales platforms, CRM systems, marketing campaigns, customer support interactions, supply chain operations, and website analytics, and while this abundance of information presents enormous potential, it also creates complexity that can overwhelm teams and slow decision-making when data is fragmented across disconnected systems. Organizations often struggle to integrate these disparate datasets, relying on manual consolidation, inconsistent formats, or isolated dashboards, which leads to delays, inaccuracies, and missed opportunities.`,
      `Unifying multi-source data through AI-powered analytics platforms transforms this challenge into a strategic advantage by aggregating, cleansing, and correlating information from all relevant systems, providing a single source of truth that delivers clarity and actionable insights across the organization. When sales, marketing, operations, and finance data are seamlessly combined, patterns emerge that would be impossible to detect in isolation, empowering leadership to make more informed, data-driven decisions.`
    ],
  },
  {
    tag: 'Security',
    title: 'Why Enterprise Security Matters in AI Analytics',
    desc: 'The foundations of secure AI: protecting data, ensuring compliance, and maintaining model integrity.',
    date: 'Feb 1, 2026',
    readTime: '8 min read',
    featured: false,
    image: process.env.PUBLIC_URL + '/blog (6)_result.webp',
    content: [
      `In today’s business landscape, data has become one of the most valuable assets an organization can possess, yet with increased reliance on AI-driven analytics platforms comes a heightened responsibility to protect that data, as breaches, leaks, or misuse can have devastating consequences for both the company and its customers, making enterprise security a top priority. AI analytics platforms process large volumes of sensitive information, including financial records, customer behavior, operational metrics, intellectual property, and strategic plans, and without robust security measures, this data is vulnerable to cyberattacks, insider threats, or accidental exposure.`,
      `Enterprise security in AI analytics is not just about preventing breaches; it is about establishing a framework of policies, tools, and practices that ensures data integrity, confidentiality, and availability, allowing organizations to leverage the power of AI confidently without exposing themselves to unnecessary risk. Modern AI platforms incorporate advanced security features such as end-to-end encryption, access controls, anomaly detection, and multi-factor authentication to protect sensitive information, ensuring that only authorized personnel can access critical data while monitoring for unusual activity that may indicate potential threats or misuse.`
    ],
  },
  {
    tag: 'No-Code',
    title: 'No-Code AI for Business Teams',
    desc: 'Democratizing AI: how no-code platforms empower non-technical teams to build and deploy models quickly.',
    date: 'Jan 28, 2026',
    readTime: '7 min read',
    featured: false,
    image: process.env.PUBLIC_URL + '/blog (7)_result.webp',
    content: [
      `Artificial intelligence is transforming the way businesses operate, enabling faster decisions, smarter insights, and predictive capabilities that were previously reserved for organizations with large data science teams, yet many companies struggle to adopt AI because traditional solutions often require technical expertise, coding skills, and specialized knowledge that most business teams do not possess, creating a barrier between potential and execution. No-code AI platforms remove that barrier, allowing business professionals, operations managers, marketers, and analysts to harness the power of AI without writing a single line of code, which democratizes access to advanced analytics and empowers teams to act on data directly.`,
      `With no-code AI, users can build predictive models, automate workflows, and generate actionable insights by simply configuring intuitive interfaces, dragging and dropping data modules, and selecting predefined algorithms, which transforms what was once a technical and time-consuming process into a fast, accessible, and highly effective workflow.`
    ],
  },
  {
    tag: 'Edge AI',
    title: 'Edge AI and the Future of Decision Intelligence',
    desc: 'Why processing data at the edge reduces latency, improves privacy, and enables real-time decisioning.',
    date: 'Jan 24, 2026',
    readTime: '9 min read',
    featured: false,
    image: process.env.PUBLIC_URL + '/blog (8)_result.webp',
    content: [
      `In an increasingly digital and connected world, businesses are producing vast amounts of data at the edge, from IoT devices, mobile applications, sensors, and operational systems, and while cloud-based AI solutions have traditionally processed this data centrally, the rise of edge AI is transforming how decisions are made by bringing intelligence closer to where data is generated, enabling faster, smarter, and more context-aware insights. Edge AI refers to deploying machine learning and analytics capabilities directly on devices or local infrastructure, rather than relying solely on cloud processing, which reduces latency, ensures real-time responsiveness, and allows organizations to act immediately on critical information.`,
      `By processing data locally, edge AI minimizes the delays associated with transmitting large datasets to central servers, which is especially important for industries where milliseconds matter, such as autonomous vehicles, industrial automation, healthcare monitoring, and high-frequency trading, and this speed advantage empowers businesses to respond instantly to changing conditions and make proactive decisions.`
    ],
  },
];

const INITIAL_COUNT = 4;
const LOAD_COUNT = 2;

/* ── Blog post detail modal ── */
const BlogModal = ({ post, onClose }) => (
  <motion.div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
    <motion.div
      className="relative glass-card rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto z-10"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header image area */}
      <div
        className="h-48 bg-gradient-to-br from-dark-card to-gray-accent relative overflow-hidden rounded-t-2xl"
        style={post.image ? { backgroundImage: `url("${encodeURI(post.image)}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
      >
        <div className="absolute inset-0 bg-primary/5" />
        {!post.image && (
          <div className="absolute inset-0 flex items-center justify-center text-primary/30">
            <div className="w-20 h-20">{icons.brain}</div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-card via-transparent to-transparent" />
        {post.featured && (
          <div className="absolute top-4 left-4">
            <span className="px-3 py-1 bg-primary text-black text-xs font-bold rounded-full">Featured</span>
          </div>
        )}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-gray-300 hover:text-white transition-colors"
          aria-label="Close"
        >
          {icons.close}
        </button>
      </div>

      {/* Content */}
      <div className="p-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-xs font-mono rounded">{post.tag}</span>
          <span className="text-xs text-gray-500">{post.readTime}</span>
          <span className="text-xs text-gray-500">{post.date}</span>
        </div>

        <h2 className="text-2xl font-bold mb-4 leading-tight">{post.title}</h2>

        <p className="text-gray-300 leading-relaxed mb-6">{post.desc}</p>

        {/* Article content from post.content (array of paragraphs) */}
        <div className="space-y-4 text-gray-400 text-sm leading-relaxed">
          {post.content && post.content.map((block, i) => (
            <p key={i} className="whitespace-pre-wrap">{block}</p>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-dark-border/40">
          {['AI', 'Analytics', 'Machine Learning', 'Data'].map((tag) => (
            <span key={tag} className="px-3 py-1 bg-primary/10 border border-primary/20 rounded-full text-xs text-primary">{tag}</span>
          ))}
        </div>
      </div>
    </motion.div>
  </motion.div>
);

/* ── Featured hero card ── */
const FeaturedPost = ({ post, onSelect }) => (
  <motion.article
    className="glass-card rounded-2xl overflow-hidden group cursor-pointer mb-10"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    whileHover={{ y: -4 }}
    onClick={() => onSelect(post)}
  >
    <div className="grid lg:grid-cols-2">
      {/* Image area */}
      <div
        className="relative h-64 lg:h-auto bg-gradient-to-br from-dark-card to-gray-accent overflow-hidden"
        style={post.image ? { backgroundImage: `url("${encodeURI(post.image)}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
      >
        <div className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors" />
        {!post.image && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary/30 group-hover:text-primary/60 group-hover:scale-110 transition-all duration-500">
              {icons.brain}
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-card/90 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-dark-card/50" />
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 bg-primary text-black text-xs font-bold rounded-full">Featured</span>
        </div>
      </div>
      {/* Content */}
      <div className="p-8 lg:p-10 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-4">
          <span className="px-2.5 py-0.5 bg-primary/10 text-primary text-xs font-mono rounded">{post.tag}</span>
          <span className="text-xs text-gray-500">{post.readTime}</span>
        </div>
        <h3 className="text-2xl lg:text-3xl font-bold mb-3 group-hover:text-primary transition-colors leading-tight">
          {post.title}
        </h3>
        <p className="text-gray-400 leading-relaxed mb-5">{post.desc}</p>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{post.date}</span>
          <span className="text-primary font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
            Read Article {icons.arrowRight}
          </span>
        </div>
      </div>
    </div>
  </motion.article>
);

/* ── Post card ── */
const PostCard = ({ post, index, onSelect }) => (
  <motion.article
    className="glass-card rounded-2xl overflow-hidden group cursor-pointer"
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    whileHover={{ y: -6 }}
    onClick={() => onSelect(post)}
  >
    {/* Thumbnail */}
    <div
      className="h-44 bg-gradient-to-br from-dark-card to-gray-accent relative overflow-hidden"
      style={post.image ? { backgroundImage: `url("${encodeURI(post.image)}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
    >
      <div className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors" />
      {!post.image && (
        <div className="absolute inset-0 flex items-center justify-center text-primary/20 group-hover:text-primary/40 transition-colors">
          {icons.brain}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-dark-card/80 to-transparent" />
    </div>
    {/* Content */}
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-mono rounded">{post.tag}</span>
        <span className="text-xs text-gray-500">{post.readTime}</span>
      </div>
      <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors leading-snug">{post.title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-2">{post.desc}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{post.date}</span>
        <span className="text-primary text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
          Read More {icons.arrowRight}
        </span>
      </div>
    </div>
  </motion.article>
);

/* ── Sidebar ── */
const Sidebar = ({ activeCategory, setActiveCategory }) => (
  <aside className="hidden lg:block w-64 flex-shrink-0">
    <div className="sticky top-28">
      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Categories</h4>
      <div className="flex flex-col gap-2">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`text-left px-3 py-2 rounded-lg text-sm transition-all ${
              activeCategory === cat
                ? 'bg-primary/15 text-primary font-medium'
                : 'text-gray-400 hover:text-white hover:bg-dark-card'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mt-8 mb-4">Popular Tags</h4>
      <div className="flex flex-wrap gap-2">
        {['AI', 'ML', 'Analytics', 'Big Data', 'Cloud', 'IoT', 'NLP', 'Security'].map((tag) => (
          <span key={tag} className="px-3 py-1 bg-primary/10 border border-primary/20 rounded-full text-xs text-primary cursor-pointer hover:bg-primary/20 transition-colors">
            {tag}
          </span>
        ))}
      </div>
    </div>
  </aside>
);

/* ── Mobile category scroller ── */
const MobileCategories = ({ activeCategory, setActiveCategory }) => (
  <div className="lg:hidden mb-8 -mx-4 px-4">
    <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
      {categories.map((cat) => (
        <button
          key={cat}
          onClick={() => setActiveCategory(cat)}
          className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            activeCategory === cat
              ? 'bg-primary text-black'
              : 'bg-dark-card border border-dark-border text-gray-400'
          }`}
        >
          {cat}
        </button>
      ))}
    </div>
  </div>
);

/* ── Main ── */
const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [selectedPost, setSelectedPost] = useState(null);

  const featured = allPosts.find((p) => p.featured);
  const filtered = useMemo(() => {
    const nonFeatured = allPosts.filter((p) => !p.featured);
    return activeCategory === 'All'
      ? nonFeatured
      : nonFeatured.filter((p) => p.tag === activeCategory);
  }, [activeCategory]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const loadMore = useCallback(() => {
    setVisibleCount((c) => c + LOAD_COUNT);
  }, []);

  // Reset count on category change
  const handleCategoryChange = useCallback((cat) => {
    setActiveCategory(cat);
    setVisibleCount(INITIAL_COUNT);
  }, []);

  return (
    <>
      <Section id="blog" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag="Insights"
          title={<>Latest from Our <span className="gradient-text">Research Lab</span></>}
          desc="Thought leadership, case studies, and engineering deep-dives from the OpticVance team."
        />

        {/* Featured post */}
        {featured && activeCategory === 'All' && <FeaturedPost post={featured} onSelect={setSelectedPost} />}

        {/* Mobile categories */}
        <MobileCategories activeCategory={activeCategory} setActiveCategory={handleCategoryChange} />

        {/* Content + Sidebar */}
        <div className="flex gap-12">
          {/* Posts grid */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                className="grid sm:grid-cols-2 gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {visible.map((post, i) => (
                  <PostCard key={post.title} post={post} index={i} onSelect={setSelectedPost} />
                ))}
              </motion.div>
            </AnimatePresence>

            {visible.length === 0 && (
              <p className="text-center text-gray-500 py-12">No posts in this category yet.</p>
            )}

            {/* Load more */}
            {hasMore && (
              <motion.div
                className="text-center mt-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <button
                  onClick={loadMore}
                  className="px-8 py-3 rounded-xl border border-primary/30 text-primary text-sm font-medium hover:bg-primary/10 transition-colors"
                >
                  Load More Articles
                </button>
              </motion.div>
            )}
          </div>

          {/* Desktop sidebar */}
          <Sidebar activeCategory={activeCategory} setActiveCategory={handleCategoryChange} />
        </div>
      </div>
      </Section>

      {/* Blog post modal — rendered outside Section to avoid transform stacking context breaking fixed positioning */}
      <AnimatePresence>
        {selectedPost && <BlogModal post={selectedPost} onClose={() => setSelectedPost(null)} />}
      </AnimatePresence>
    </>
  );
};

export default Blog;
